import { useBasicSettingsData } from "api/basic-settings";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useMember } from "api/members";
import { CustomAttributesSection } from "components/CustomAttributesSection/CustomAttributesSection";
import { FormButtons } from "components/FormControls/FormButtons";
import { useStyles } from "components/Layout/Member/MemberForm/MemberForm.styles";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate } from "react-router-dom";
import {
  BackendMemberModel,
  MemberFeFields,
  MemberPayload,
} from "types/members";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { AddressDetails } from "./AddressDetails/AddressDetails";
import { Agreements } from "./Agreements/Agreements";
import { BasicInformation } from "./BasicInformation/BasicInformation";
import { CompanyDetails } from "./CompanyDetails/CompanyDetails";

export function MemberForm(props: {
  editMode?: boolean;
  initialValues?: BackendMemberModel;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const { addMember, editMember, useMembersData } = useMember(
    props.initialValues?.referrerCustomerId,
  );
  const { data: referrerMember } = useMembersData();

  const basicSettings = useBasicSettingsData("general");
  const identificationMethod =
    basicSettings?.otherValues?.customersIdentificationConfigurations?.find(
      (config) => config.identificationMethod,
    )?.field;

  const initialValues: MemberFeFields =
    props.editMode && props.initialValues
      ? {
          customerId: props.initialValues.customerId,
          agreement1: props.initialValues.agreement1,
          email: props.initialValues.email,
          firstName: props.initialValues.firstName,
          lastName: props.initialValues.lastName,
          address: props.initialValues.address,
          agreement2: props.initialValues.agreement2,
          agreement3: props.initialValues.agreement3,
          birthDate: props.initialValues.birthDate || null,
          company: {
            name: props.initialValues.company?.name,
            nip: props.initialValues.company?.nip,
          },
          gender: props.initialValues.gender,
          labels: props.initialValues.labels || [],
          levelId: props.initialValues.currentLevel?.levelId,
          loyaltyCardNumber: props.initialValues.loyaltyCardNumber,
          phone: props.initialValues.phone,
          referralCustomerEmail: props.initialValues.referralCustomerEmail,
          companyDetails:
            props.initialValues.company &&
            Object.keys(props.initialValues.company).length !== 0,
          addressDetails:
            props.initialValues.address &&
            Object.keys(props.initialValues.address).length !== 0,
          referrerToken: referrerMember?.referralToken,
          registeredAt: props.initialValues.registeredAt,
        }
      : {
          customerId: "",
          address: undefined,
          addressDetails: false,
          agreement1: false,
          agreement2: false,
          agreement3: false,
          birthDate: null,
          company: { name: "", nip: "" },
          companyDetails: false,
          email: "",
          firstName: "",
          gender: "not_disclosed",
          labels: [],
          lastName: "",
          levelId: undefined,
          loyaltyCardNumber: undefined,
          phone: undefined,
          referralCustomerEmail: undefined,
          referrerToken: undefined,
          registeredAt: null,
        };

  return (
    <>
      <Formik
        enableReinitialize
        initialStatus={{}}
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          delete values.avatarPath;
          setSubmitting(true);
          setStatus({});
          const properties: MemberPayload = {
            ...(({
              companyDetails,
              addressDetails,
              avatar,
              customerId,
              ...customer
            }) => customer)(values),
            address: values.addressDetails ? values.address : undefined,
            company: values.companyDetails ? values.company : undefined,
          };
          try {
            const response =
              props.editMode && props.initialValues
                ? await editMember(props.initialValues.customerId, properties)
                : await addMember(properties);

            if (response?.data?.customerId) {
              navigate(links.singleMemberGeneralInfo(response.data.customerId));
              if (props.editMode) {
                notify(message({ id: "memberProfileUpdated" }));
              } else {
                notify(message({ id: "memberAdded" }));
              }
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({
          handleSubmit,
          isSubmitting,
          status,
          values,
          dirty,
          initialValues,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              {status?.[""] && (
                <span className={classes.error}>{status[""]}</span>
              )}

              <BasicInformation
                identificationMethod={identificationMethod}
                isEditMode={!!props.editMode}
                tier={values.levelId}
              />
              <CustomAttributesSection labels={values.labels || []} />
              <AddressDetails showAddressDetails={!!values.addressDetails} />
              <CompanyDetails showAddressDetails={!!values.companyDetails} />
              <Agreements />
              {!isSubmitting && (
                <LeaveSiteGuard
                  warnBeforeRefresh
                  isDirty={dirty}
                  initialValues={initialValues}
                  currentValues={values}
                />
              )}

              <FormButtons
                disableSubmit={isSubmitting}
                onCancelClick={() => navigate(links.membersList)}
                onSubmitClick={() => handleSubmit()}
                {...(!props.editMode && { submitMessage: "addMember" })}
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
